'use client';

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

export interface GallerySlot {
  src: string;
  onLoad: () => void;
  onError: () => void;
  hidden: boolean;
}

interface UseGalleryImagesResult {
  mainSlot: GallerySlot | null;
  thumbSlots: GallerySlot[];
  extraCount: number;
  isReady: boolean;
  isEmpty: boolean;
}

const THUMB_COUNT = 6;
const TIMEOUT_MS = 10000;

export function useGalleryImages(
  imageList:
    | Array<
        { imageMidUrl?: string; imageFullUrl?: string; imageTmbUrl?: string } | null | undefined
      >
    | null
    | undefined,
): UseGalleryImagesResult {
  const allSrcs = useMemo(() => {
    if (!imageList || imageList.length === 0) return [];
    return imageList
      .map((img) => img?.imageMidUrl || img?.imageFullUrl || img?.imageTmbUrl || '')
      .filter(Boolean) as string[];
  }, [imageList]);

  const total = allSrcs.length;
  const srcsKey = allSrcs.join('|');

  // Per-src status: broken srcs are hidden, loaded srcs are shown, pending = unknown
  const [brokenSet, setBrokenSet] = useState<Set<string>>(() => new Set());
  const [loadedSet, setLoadedSet] = useState<Set<string>>(() => new Set());

  // Refs for timeout callback (avoid stale closures)
  const allSrcsRef = useRef(allSrcs);
  allSrcsRef.current = allSrcs;
  const brokenRef = useRef(brokenSet);
  brokenRef.current = brokenSet;
  const loadedRef = useRef(loadedSet);
  loadedRef.current = loadedSet;

  // Reset state when imageList changes
  const prevSrcsKey = useRef<string>('');
  if (prevSrcsKey.current !== srcsKey) {
    prevSrcsKey.current = srcsKey;
    if (brokenSet.size > 0) setBrokenSet(new Set());
    if (loadedSet.size > 0) setLoadedSet(new Set());
  }

  const settledCount = brokenSet.size + loadedSet.size;
  const isReady = total === 0 || settledCount >= total;

  // Timeout: mark all still-pending srcs as broken so no empty holes remain
  useEffect(() => {
    if (total === 0) return;
    const timer = setTimeout(() => {
      const srcs = allSrcsRef.current;
      const broken = brokenRef.current;
      const loaded = loadedRef.current;
      const pending = srcs.filter((src) => !broken.has(src) && !loaded.has(src));
      if (pending.length === 0) return;
      setBrokenSet((prev) => {
        const next = new Set(prev);
        pending.forEach((src) => next.add(src));
        return next;
      });
    }, TIMEOUT_MS);
    return () => clearTimeout(timer);
  }, [srcsKey, total]);

  const handleLoad = useCallback((src: string) => {
    setLoadedSet((prev) => {
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }, []);

  const handleError = useCallback((src: string) => {
    setBrokenSet((prev) => {
      const next = new Set(prev);
      next.add(src);
      return next;
    });
  }, []);

  const isEmpty = total === 0 || allSrcs.every((src) => brokenSet.has(src));

  const buildSlot = useCallback(
    (src: string): GallerySlot => ({
      src,
      hidden: brokenRef.current.has(src),
      onLoad: () => handleLoad(src),
      onError: () => handleError(src),
    }),
    [handleLoad, handleError],
  );

  const mainSlot = useMemo(
    () => (allSrcs.length > 0 ? buildSlot(allSrcs[0]) : null),
    [allSrcs, buildSlot, brokenSet],
  );

  const thumbSlots = useMemo(
    () => allSrcs.slice(1, THUMB_COUNT + 1).map(buildSlot),
    [allSrcs, buildSlot, brokenSet],
  );

  // Extra = all images beyond what we display (1 main + 6 thumbs).
  // We don't render <img> for these at all, so they can never fire onLoad/onError
  // and must not be filtered by brokenSet (timeout would wrongly mark them broken).
  const extraCount = useMemo(() => Math.max(0, allSrcs.length - (THUMB_COUNT + 1)), [allSrcs]);

  return { mainSlot, thumbSlots, extraCount, isReady, isEmpty };
}
